import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { TripDetailsV2 } from "@b2bportal/air-shopping-api";
import {
  CallState,
  type FlightFareSelectionFunctionParams,
  type IFareTrip,
} from "@b2bportal/core-types";
import {
  getFareSliceRating,
  filterWithFareShelfRatings,
} from "@b2bportal/core-utilities";
import {
  FlightCardDetailsLoading,
  FlightCardFareSelector,
  type FlightCardFareSelectorProps,
} from "@tenant/flights";
import { passengerCountSelector } from "../../../search/store/selectors";
import { useFlightFares } from "../../hooks";
import { selectedTripSelector } from "../../store/selectors";

export type FlightFaresLoaderProps = Omit<
  FlightCardFareSelectorProps,
  "faresSelection"
> & {
  trip: TripDetailsV2;
  fareTrips?: IFareTrip[];
  fareShelfFilter?: number[];
  onFareSelect: (
    params: Omit<FlightFareSelectionFunctionParams, "sliceId">
  ) => void;
};

/**
 * Loads and displays fares for a flight card.
 * Filters fares based on:
 * 1. fareShelfFilter (when user clicks FlightFareGrid items)
 * 2. outgoingFareRating (cross-page: departure selection filters return page)
 */
export const FlightFaresLoader = (props: FlightFaresLoaderProps) => {
  const { trip, fareTrips, onFareSelect, fareShelfFilter, sliceIndex } = props;
  const passengers = useSelector(passengerCountSelector);

  // Get selected fare from departure page (for cross-page filtering)
  const selectedTrip = useSelector(selectedTripSelector);
  const outgoingFareRating = selectedTrip?.outgoingFareRating;

  const fares = useFlightFares({
    trip,
    passengers,
    fareTrips,
    onFareSelect,
  });

  // Filter fares to only show selected fare class and higher
  const filteredFares = useMemo(() => {
    if (fares.state !== CallState.Success) return fares;

    const filtered = fares.data.fares.filter((fare) => {
      // Add bounds checking before accessing slices
      const fareSlice = fare.fareDetails.slices[sliceIndex];
      if (!fareSlice) return false;

      const fareSliceRating = getFareSliceRating(fareSlice);

      // Scenario 1: User clicked a fare grid item on current card
      if (fareShelfFilter && fareShelfFilter.length > 0) {
        const matchesGridFilter = filterWithFareShelfRatings(
          fareShelfFilter,
          fareSliceRating
        );

        // Scenario 2: On return page, also check departure selection
        if (
          sliceIndex === 1 &&
          outgoingFareRating !== undefined &&
          outgoingFareRating >= 0
        ) {
          return matchesGridFilter && fareSliceRating >= outgoingFareRating;
        }

        return matchesGridFilter;
      }

      // No grid filter, but check departure selection on return page
      if (
        sliceIndex === 1 &&
        outgoingFareRating !== undefined &&
        outgoingFareRating >= 0
      ) {
        return fareSliceRating >= outgoingFareRating;
      }

      // No filters applied
      return true;
    });

    // Handle empty results - return original fares if filtering removes everything
    if (filtered.length === 0) return fares;

    return {
      ...fares,
      data: {
        ...fares.data,
        fares: filtered,
      },
    };
  }, [fares, fareShelfFilter, sliceIndex, outgoingFareRating]);

  switch (filteredFares.state) {
    case CallState.Success:
      return (
        <FlightCardFareSelector
          {...props}
          faresSelection={filteredFares.data}
        />
      );
    case CallState.Failed:
      return null;
    case CallState.InProcess:
    case CallState.NotCalled:
      return <FlightCardDetailsLoading />;
  }
};
